(() => {
  if (customElements.get('bestseller-carousel')) return;

  class BestsellerCarousel extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      this.layout = this.dataset.layout || 'carousel';
      this.viewport = this.querySelector('[data-bsc-viewport]');
      this.track = this.querySelector('[data-bsc-track]');
      this.prevBtn = this.querySelector('[data-bsc-prev]');
      this.nextBtn = this.querySelector('[data-bsc-next]');

      if (!this.viewport || !this.track) return;

      this.originalSlides = Array.from(this.track.children);
      this.dragEnabled = this.dataset.drag === 'true';
      this.infinite = this.dataset.infinite === 'true';

      if (this.layout === 'grid') {
        this.bindAddToCart();
        return;
      }

      if (this.infinite && this.originalSlides.length > 0) {
        this.setupInfiniteClones();
      }

      this.bindArrows();
      if (this.dragEnabled) this.bindDrag();
      this.bindScroll();
      this.bindAddToCart();

      window.addEventListener('resize', this.debounce(() => this.recenter(), 150));
      // Initial position
      requestAnimationFrame(() => this.recenter(true));
    }

    /* ------------ Infinite loop clones ------------ */
    setupInfiniteClones() {
      const slides = this.originalSlides;
      if (slides.length < 2) return;

      this.cloneCount = Math.min(slides.length, 6);

      const prepClone = (node) => {
        const clone = node.cloneNode(true);
        clone.setAttribute('data-clone', 'true');
        // Strip ids from cloned elements to avoid duplicate ids in the DOM
        // (cloned forms inherit the same `id` otherwise, which confuses the
        // browser when submitting and breaks label/aria associations).
        clone.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
        return clone;
      };

      // Clone last N to the front and first N to the back
      for (let i = slides.length - this.cloneCount; i < slides.length; i++) {
        this.track.insertBefore(prepClone(slides[i]), this.track.firstChild);
      }
      for (let i = 0; i < this.cloneCount; i++) {
        this.track.appendChild(prepClone(slides[i]));
      }
    }

    recenter(instant = false) {
      if (!this.infinite || !this.cloneCount) return;
      const firstOriginal = this.track.children[this.cloneCount];
      if (!firstOriginal) return;
      const behavior = this.viewport.style.scrollBehavior;
      this.viewport.style.scrollBehavior = 'auto';
      this.viewport.scrollLeft = firstOriginal.offsetLeft - this.getPeek();
      if (!instant) {
        void this.viewport.offsetWidth; // reflow
      }
      this.viewport.style.scrollBehavior = behavior;
    }

    getPeek() {
      const cs = getComputedStyle(this.viewport);
      return parseFloat(cs.paddingLeft) || 0;
    }

    getSlideWidth() {
      const slide = this.track.children[0];
      if (!slide) return 0;
      const style = getComputedStyle(this.track);
      const gap = parseFloat(style.columnGap || style.gap) || 0;
      return slide.getBoundingClientRect().width + gap;
    }

    /* ------------ Arrow buttons ------------ */
    bindArrows() {
      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', () => this.scrollByCol(-1));
      }
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', () => this.scrollByCol(1));
      }
    }

    scrollByCol(direction) {
      const slideW = this.getSlideWidth();
      if (!slideW) return;
      this.viewport.scrollBy({
        left: slideW * direction,
        behavior: 'smooth',
      });
    }

    /* ------------ Scroll handling (infinite wrap + arrow state) ------------ */
    bindScroll() {
      let raf;
      this.viewport.addEventListener('scroll', () => {
        if (raf) cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => this.handleScroll());
      });
    }

    handleScroll() {
      if (this.infinite && this.cloneCount) {
        const slideW = this.getSlideWidth();
        if (!slideW) return;
        const scrollLeft = this.viewport.scrollLeft;
        const total = this.track.children.length;
        const originalLen = this.originalSlides.length;

        const firstRealLeft = this.track.children[this.cloneCount].offsetLeft - this.getPeek();
        const lastRealLeft = this.track.children[this.cloneCount + originalLen - 1].offsetLeft - this.getPeek();

        // If we drifted into the cloned-before region, jump to the equivalent real slide
        if (scrollLeft < firstRealLeft - slideW * 0.5) {
          const delta = firstRealLeft - scrollLeft;
          this.jumpTo(lastRealLeft + slideW - delta);
        } else if (scrollLeft > lastRealLeft + slideW * 0.5) {
          const delta = scrollLeft - (lastRealLeft + slideW);
          this.jumpTo(firstRealLeft + delta);
        }
      } else {
        this.updateArrowState();
      }
    }

    jumpTo(position) {
      const behavior = this.viewport.style.scrollBehavior;
      this.viewport.style.scrollBehavior = 'auto';
      this.viewport.scrollLeft = position;
      this.viewport.style.scrollBehavior = behavior;
    }

    updateArrowState() {
      if (this.infinite) return;
      if (!this.prevBtn || !this.nextBtn) return;
      const sl = this.viewport.scrollLeft;
      const max = this.viewport.scrollWidth - this.viewport.clientWidth;
      this.prevBtn.disabled = sl <= 1;
      this.nextBtn.disabled = sl >= max - 1;
    }

    /* ------------ Drag / swipe ------------ */
    bindDrag() {
      // Let native touch scrolling handle mobile swipe. Custom drag is mouse-only
      // so we don't hijack clicks or form submissions on the button / image link.
      const DRAG_THRESHOLD = 8;

      let pointerDown = false;
      let isDragging = false;
      let startX = 0;
      let startScroll = 0;
      let pointerId = null;

      const onDown = (e) => {
        if (e.pointerType !== 'mouse') return;
        if (e.button !== 0) return;
        pointerDown = true;
        isDragging = false;
        startX = e.clientX;
        startScroll = this.viewport.scrollLeft;
        pointerId = e.pointerId;
      };

      const onMove = (e) => {
        if (!pointerDown) return;
        const dx = e.clientX - startX;

        if (!isDragging) {
          if (Math.abs(dx) <= DRAG_THRESHOLD) return;
          // Upgrade to a real drag only after the threshold is exceeded.
          // Capture happens here (not on pointerdown) so small taps still
          // deliver click events to the underlying button / link natively.
          isDragging = true;
          this.viewport.classList.add('is-dragging');
          try { this.viewport.setPointerCapture(pointerId); } catch (_) {}
        }

        this.viewport.scrollLeft = startScroll - dx;
        e.preventDefault();
      };

      const onUp = () => {
        if (!pointerDown) return;
        const wasDragging = isDragging;
        pointerDown = false;
        isDragging = false;
        this.viewport.classList.remove('is-dragging');
        if (pointerId != null) {
          try { this.viewport.releasePointerCapture(pointerId); } catch (_) {}
          pointerId = null;
        }
        if (wasDragging) {
          // Suppress the synthetic click that follows a drag so buttons and
          // links don't fire after the user "dragged" across them.
          this.viewport.classList.add('was-dragging');
          setTimeout(() => this.viewport.classList.remove('was-dragging'), 60);
          this.snapToNearest();
        }
      };

      this.viewport.addEventListener('pointerdown', onDown);
      this.viewport.addEventListener('pointermove', onMove);
      this.viewport.addEventListener('pointerup', onUp);
      this.viewport.addEventListener('pointercancel', onUp);

      this.viewport.addEventListener('click', (e) => {
        if (this.viewport.classList.contains('was-dragging')) {
          e.preventDefault();
          e.stopPropagation();
        }
      }, true);

      this.viewport.addEventListener('dragstart', (e) => e.preventDefault());
    }

    snapToNearest() {
      const slideW = this.getSlideWidth();
      if (!slideW) return;
      const peek = this.getPeek();
      const sl = this.viewport.scrollLeft;
      const idx = Math.round((sl + peek) / slideW);
      const target = idx * slideW - peek;
      this.viewport.scrollTo({ left: target, behavior: 'smooth' });
    }

    /* ------------ Add to cart ------------ */
    bindAddToCart() {
      this.querySelectorAll('form.bsc-form').forEach((form) => {
        form.addEventListener('submit', (e) => {
          e.preventDefault();
          this.submitAddToCart(form);
        });
      });
    }

    async submitAddToCart(form) {
      const button = form.querySelector('.bsc-card__atc');
      if (!button || button.disabled) return;

      button.classList.add('is-loading');

      const cart =
        document.querySelector('cart-notification') ||
        document.querySelector('cart-drawer');

      const config = (typeof fetchConfig === 'function' ? fetchConfig('javascript') : { method: 'POST', headers: {} });
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];

      const formData = new FormData(form);
      if (cart && typeof cart.getSectionsToRender === 'function') {
        formData.append(
          'sections',
          cart.getSectionsToRender().map((s) => s.id)
        );
        formData.append('sections_url', window.location.pathname);
        if (typeof cart.setActiveElement === 'function') cart.setActiveElement(document.activeElement);
      }
      config.body = formData;

      try {
        const base = (window.routes && window.routes.cart_add_url) || '/cart/add';
        const response = await fetch(base, config);
        const data = await response.json();

        if (data.status) {
          this.handleErrorMessage(form, data.description || data.message);
          return;
        }

        if (!cart) {
          window.location = (window.routes && window.routes.cart_url) || '/cart';
          return;
        }

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'bestseller-carousel',
            productVariantId: formData.get('id'),
          });
        }

        cart.renderContents(data);
      } catch (err) {
        console.error('[bestseller-carousel] ATC failed', err);
      } finally {
        button.classList.remove('is-loading');
      }
    }

    handleErrorMessage(form, message) {
      const wrapper = form.querySelector('.bsc-card__atc-error');
      const textEl = form.querySelector('.bsc-card__atc-error-text');
      if (!wrapper || !textEl) return;
      if (message) {
        wrapper.hidden = false;
        textEl.textContent = message;
      } else {
        wrapper.hidden = true;
        textEl.textContent = '';
      }
    }

    /* ------------ Utilities ------------ */
    debounce(fn, wait) {
      let t;
      return (...args) => {
        clearTimeout(t);
        t = setTimeout(() => fn.apply(this, args), wait);
      };
    }
  }

  customElements.define('bestseller-carousel', BestsellerCarousel);
})();
